import type { TFunction } from "i18next";
import {
  EMPLOYMENT_TYPE_VALUES,
  EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
} from "model/OrganizationalStructure";
import { toEmploymentTypeLabel } from "web-apps/company/components/common/employmentType/employmentType.logic";

export interface SharedSelectOption {
  value: string;
  label: string;
}

interface SelectedOptionLabelParams {
  readonly options: readonly SharedSelectOption[];
  readonly value: string | undefined;
}

interface EmploymentTypeOptionsParams {
  readonly canSelectSimflexityEmployee?: boolean;
  readonly translate: TFunction | ((key: string) => string);
}

export function getSelectedOptionLabel({
  options,
  value,
}: SelectedOptionLabelParams): string {
  return options.find((option) => option.value === value)?.label ?? value ?? "";
}

/** Skatteverket numbers its tax tables 29 through 40. */
const FIRST_TAX_TABLE = 29;
const LAST_TAX_TABLE = 40;

export const STAFF_TAX_TABLE_OPTIONS: SharedSelectOption[] = Array.from(
  { length: LAST_TAX_TABLE - FIRST_TAX_TABLE + 1 },
  (_, index) => {
    const value = FIRST_TAX_TABLE + index;
    return {
      value: String(value),
      label: String(value),
    };
  },
);

/**
 * Handles to translated options.
 */
export const toTranslatedOptions = (
  options: Array<{ value: string; label: string }>,
  translate: TFunction | ((key: string) => string),
): SharedSelectOption[] => {
  return options.map((option) => ({
    value: option.value,
    label: translate(option.label),
  }));
};

/**
 * Handles translated employment options using Staff employment type labels.
 */
export function toEmploymentTypeOptions({
  canSelectSimflexityEmployee = true,
  translate,
}: EmploymentTypeOptionsParams): SharedSelectOption[] {
  return EMPLOYMENT_TYPE_VALUES.flatMap((value) => {
    if (
      !canSelectSimflexityEmployee &&
      value === EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE
    ) {
      return [];
    }
    return {
      value,
      label: toEmploymentTypeLabel({ employmentType: value, translate }),
    };
  });
}
